import { useEffect, useRef, useState } from 'react';
import { getSoundSettings, onSoundSettings, play, setSoundSettings, type SoundSettings } from './sound';

function useSoundSettings(): SoundSettings {
  const [s, setS] = useState(getSoundSettings);
  useEffect(() => onSoundSettings(setS), []);
  return s;
}

function Speaker({ muted }: { muted: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
      <path d="M4 9 H8 L13 5 V19 L8 15 H4 Z" fill="currentColor" />
      {muted ? (
        <path d="M16 9 L21 14 M21 9 L16 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      ) : (
        <path d="M16 8.5 C18 10 18 14 16 15.5 M18.5 6 C22 9 22 15 18.5 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      )}
    </svg>
  );
}

const SLIDERS: { key: 'master' | 'music' | 'sfx' | 'ui'; label: string }[] = [
  { key: 'master', label: 'Geral' },
  { key: 'music', label: 'Música' },
  { key: 'sfx', label: 'Efeitos' },
  { key: 'ui', label: 'Interface' },
];

/** Botão de mudo sempre visível e um painel com os volumes (salvos no navegador). */
export function SoundControls() {
  const s = useSoundSettings();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  return (
    <div className="sound-ctl" ref={ref}>
      <button
        className="btn ghost small"
        onClick={() => setSoundSettings({ muted: !s.muted })}
        aria-pressed={s.muted}
        title={s.muted ? 'Ligar o som' : 'Silenciar'}
        data-silent
      >
        <Speaker muted={s.muted} />
        <span className="visually-hidden">{s.muted ? 'Ligar o som' : 'Silenciar'}</span>
      </button>
      <button className="btn ghost small" onClick={() => setOpen(!open)} aria-expanded={open} aria-haspopup="dialog">
        Som
      </button>
      {open && (
        <div className="sound-pop" role="dialog" aria-label="Volume">
          {SLIDERS.map(({ key, label }) => (
            <label key={key} className="sound-row">
              <span>{label}</span>
              <input
                type="range"
                min={0}
                max={100}
                value={Math.round(s[key] * 100)}
                onChange={(e) => setSoundSettings({ [key]: Number(e.target.value) / 100 })}
                onPointerUp={() => key !== 'music' && play(key === 'ui' ? 'click' : 'peca', { bus: key === 'ui' ? 'ui' : 'sfx' })}
              />
              <span className="num">{Math.round(s[key] * 100)}</span>
            </label>
          ))}
          <label className="sound-row check">
            <input type="checkbox" checked={s.musicOn} onChange={(e) => setSoundSettings({ musicOn: e.target.checked })} />
            <span>Trilha de fundo</span>
          </label>
          <p className="hint" style={{ margin: 0 }}>
            Trilha original do Estandarte; efeitos sintetizados no navegador.
          </p>
        </div>
      )}
    </div>
  );
}
